import { ContactInfo } from './contactInfo';
import { User } from './user';
import { Address } from './address';
import BasixBase from './basixBase';

export enum WorkOrderStatus {
  Generated,
  WorkInProgress,
  WorkComplete,
  InvoicesGenerated
}

export interface WorkOrder extends BasixBase {
  costTotal: number;
  createdByName?: string;
  createdById: string;
  createdDate: string;
  customer: ContactInfo;
  customerId: string;
  customerName?: string; // flat name carried by the list index DTO (grid column accessor)
  date: string;
  internalNotes: string;
  // item 543 — the technician's "work performed" (client-facing, carries to the invoice)
  workPerformed?: string;
  trackingUrl?: string | null;
  showPerformedOnPdf?: boolean; // item 1336 — T&M output option (per-line date + who on the PDF) // item 1310 — shipment tracking link (from the PO conversion; carries to the invoice)
  isSalesOrder: boolean; // item 38: Sales Order (materials/hardware only) vs Work Order
  // item 234 (reworked) — per-PDF price-column visibility (group price + MSRP/SRP suggestion)
  showGroupPriceOnPdf?: boolean;
  showMsrpOnPdf?: boolean;
  itemCategories: string;
  lastModifiedByName?: string;
  lastModifiedById: string;
  lastModifiedByDate: string;
  // item 607 — the index DTO (WorkOrderIndexItemDto) serializes these flat fields on list
  // rows (distinct from BasixBase.isArchived); the Sales Bible reads them directly
  lastModifiedDate?: string;
  isArchive?: boolean;
  phoneNo: string;
  projectId: string;
  projectName?: string; // item 1303 — flat name carried by the list index DTO (timesheet job picker)
  projectManager: User;
  projectManagerId: string;
  projectManagerName?: string; // item 607 — PM name on index rows (Sales Bible WO rows)
  // item 1210 — Sales Bible standalone-WO rows: assigned lead names + the WO's own manual columns
  leadNames?: string[];
  deploymentDate?: string | null;
  illustrationsComplete?: string | null;
  salesBibleNotes?: string | null;
  customerProjectManagerId?: string; // item 530 — customer PM (CustomerStaff.id) → staff-level Sales PO
  customerProjectManagerName?: string; // item 612 — customer PM / staff-contact name (index rows)
  projectPointofContact: string;
  proposalId?: string;
  // item 1069 — the PO this WO/SO was converted from (item 733F); lets the pipeline stop
  // counting a purchase order once its customer-facing doc exists
  sourcePurchaseOrderId?: string;
  proposalNumber?: string; // item 553 — the linked proposal's number ("Tied to Proposal ####")
  // item 597 — the WO's own platform (inherited from the proposal when proposal-born,
  // required for a standalone WO); index rows carry the effective value (own ?? proposal)
  platform?: string | null;
  // item 561 — any non-void invoice exists (a deposit leaves the WO open, so status alone
  // can't tell); drives the grid's INVOICED state + ⋮ Create another invoice
  hasLiveInvoice?: boolean;
  // item 559/560 — approver attribution inherited from the source proposal
  clientApprovedDate?: string | null;
  clientApprovedByName?: string | null;
  approvedByName?: string | null;
  reasonForWorkOrder: string;
  requestedBy: string;
  retailSubtotal: number;
  retailTotal: number;
  salesRepName?: string;
  status: WorkOrderStatus;
  tax: number;
  taxState?: string; // item 179
  workOrderItems: WorkOrderItem[];
  workOrderNumber: string;
  workOrderTitle: string;
  // item 465 — Service Address (end-user); name is workOrderTitle, rest optional
  serviceAddress?: Address;
  servicePhone?: string;
  serviceEmail?: string;
}

export interface WorkOrderItem {
  // item 1239 — the original group price before the doc-level discount (null = undiscounted)
  preDiscountRetailEach?: number | null;
  id: string;
  category: string;
  itemId: string;
  vendorId?: string;
  sku: string;
  brand: string;
  description: string;
  costEach: number;
  retailEach: number; // the GROUP price (binding) for this line
  listPrice?: number | null; // item 234 — MSRP/SRP suggestion (display only)
  isHardware?: boolean; // item 234 — MSRP (hardware) vs SRP (services) label
  quantity: number;
  taxable: boolean;
  workPerformed: string;
  employeeId?: string;
  employee?: User;
  lineDate?: string | null; // item 1336 — per-line T&M date (carries to the invoice line)
  startTime?: string;
  endTime?: string;
  totalHours: string;
}
export interface WorkOrdersDto {
  index: WorkOrder[];
  length: number;
}
